import { defineComponent as _defineComponent } from 'vue'
import { CustomException } from '@tencent/ppd-base-libs';
import { showToast, ToastType } from '@tencent/wphk-components-toast';
import { computed, nextTick, ref } from 'vue-demi';
import {
  clearShareCodeCache,
  getIsC2CInActTime,
  getIsShowActEndTips,
  getSuccessInviteNum,
  getUrlHasC2CShareCode,
  loadC2CConfByCampaignId,
  setInviteChangeNum,
  isShowC2CDialog,
} from '../../business';
import { useActInfo } from '../../hooks/use-act-info';
import { i18n } from '../../i18n';
import { C2CConfig, C2CSharerAction, TipsDialogLogo, C2CToastType } from '../../types';
import C2CEntry from '../entry/index.vue';
import C2CMainDialog from '../main-dialog/index.vue';
import C2CTipsDialog from '../tips-dialog/index.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
  campaignId: String,
},
  emits: ['onHideC2C', 'onShowC2C', 'onInitedWithCode', 'onRestWxShare'],
  setup(__props, { expose, emit: emits }) {

const props = __props



expose({ init, showNonTargetUserDialog, afterHighAuthLoginShare, showC2CToast });

/** 配置 */
const c2cConfig = ref<C2CConfig | null>(null);
/** 是否展示C2C裂变入口 */
const isShowC2CEntry = ref(false);
/** 主弹窗 */
const mainDialog = ref<InstanceType<typeof C2CMainDialog> | null>(null);
/** 提示弹窗 */
const tipsDialog = ref<InstanceType<typeof C2CTipsDialog> | null>(null);

const { initActInfo, successInviteCount, isSelfUrlShareCode } = useActInfo();

const urlShareCode = ref('');

const disSource = computed(() => ['in', 'share', c2cConfig.value?.actInfo.actId, urlShareCode.value].join('.'));

/** 拉取配置 */
async function getConfig() {
  // 拉取 C2C 配置
  return loadC2CConfByCampaignId(props.campaignId as string);
}

/** 拉起主弹窗 */
function showC2CMainDialog() {
  mainDialog.value?.showDialog();
}

/** 拉起非目标用户提示弹窗 */
function showNonTargetUserDialog() {
  if (!c2cConfig.value?.errorTipInfo.nonTargetUserTip) return;
  tipsDialog.value?.showDialog({
    title: c2cConfig.value.errorTipInfo.nonTargetUserTip,
    btnText: i18n('立即邀請'),
    onBtnClickCb: () => {
      showC2CMainDialog();
    },
  });
}

/** 拉起成功邀请提示弹窗 */
function showInviteChangeDialog() {
  const successInviteNum = getSuccessInviteNum(successInviteCount.value);
  if (successInviteNum <= 0) return false;

  tipsDialog.value?.showDialog({
    title: i18n('你已成功邀請X位合資格好友', { sum: successInviteCount.value }),
    desc: i18n('快嚟拎獎賞！'),
    btnText: i18n('立即查看'),
    logoType: TipsDialogLogo.Hand,
    onBtnClickCb: () => {
      showC2CMainDialog();
      setInviteChangeNum(successInviteCount.value);
    },
    onCloseClickCb: () => {
      setInviteChangeNum(successInviteCount.value);
    },
  });
  return true;
}

/** 高授权回调进行分享 */
function afterHighAuthLoginShare(sharerAction: C2CSharerAction) {
  showC2CMainDialog();
  nextTick(() => {
    mainDialog.value?.onShare(sharerAction);
  });
}

/** 分享重置 */
function onRestWxShare() {
  emits('onRestWxShare');
}

/** 展示业务弹窗 */
function showBusinessDialog() {
  // 是否有分享码
  const hasShareCode = !!urlShareCode.value;

  // 没有分享码,为本人
  if (!hasShareCode) {
    // 拉起成功邀请提示弹窗
    if (showInviteChangeDialog()) return;
    // 拉起活动主弹窗
    if (isShowC2CDialog()) {
      showC2CMainDialog();
    }
    return;
  }

  // 有分享码
  if (isSelfUrlShareCode.value) {
    // 有分享码，且是本人分享码
    showC2CMainDialog();
    const hasShowInviteChangeDialog = showInviteChangeDialog();
    emits('onInitedWithCode', { hasShowInviteChangeDialog });
  } else {
    // 有分享码，且非本人分享码
    emits('onInitedWithCode', { disSource: disSource.value });
  }
}

/** 展示toast */
function showC2CToast(toastType: C2CToastType) {
  switch (toastType) {
    case C2CToastType.ActEndTips:
      showToast({
        type: ToastType.Exclamation,
        title: i18n('活動已結束<br/>可參與其它活動'),
      });
      break;
    case C2CToastType.C2CInviteeReceivedAll:
      showToast({
        type: ToastType.Tick,
        title: c2cConfig.value?.errorTipInfo.receivedAllUserTip,
      });
      break;
    case C2CToastType.C2CInviteeSelf:
      showToast({
        type: ToastType.Exclamation,
        title: i18n('邀請自己無法<br/>獲得獎賞哦'),
      });
      break;
    default:
      break;
  }
}

/** C2C活动预校验 */
async function preCheckC2CAct(): Promise<boolean> {
  // 拉取配置
  c2cConfig.value = await getConfig();
  if (!c2cConfig.value) return false;

  // 提示活动已结束
  if (getIsShowActEndTips(c2cConfig.value, urlShareCode.value)) {
    showC2CToast(C2CToastType.ActEndTips);
    clearShareCodeCache(c2cConfig.value.actInfo.actId);
    return false;
  }

  // 不在活动期间内
  if (!getIsC2CInActTime(c2cConfig.value)) return false;

  return true;
}

/** 配置&活动 初始化 */
async function init(isHighAuthLoginBack = false) {
  try {
    // 链接分享码
    urlShareCode.value = getUrlHasC2CShareCode() ?? '';

    // C2C活动预校验
    const isShowC2C = await preCheckC2CAct();

    if (!isShowC2C) {
      emits('onHideC2C');
      return;
    }

    emits('onShowC2C');

    // 处理分享入口展示
    isShowC2CEntry.value = true;

    // 拉取分享活动信息
    if (c2cConfig.value?.actInfo.actId) {
      await initActInfo(c2cConfig.value.actInfo.actId, urlShareCode.value);
    }

    if (isHighAuthLoginBack) return;

    // 处理弹窗提示
    showBusinessDialog();
  } catch (error) {
    new CustomException(error, 'initC2CFailed', '初始化C2C活动失败');
  }
}

return { __sfc: true,props, emits, c2cConfig, isShowC2CEntry, mainDialog, tipsDialog, initActInfo, successInviteCount, isSelfUrlShareCode, urlShareCode, disSource, getConfig, showC2CMainDialog, showNonTargetUserDialog, showInviteChangeDialog, afterHighAuthLoginShare, onRestWxShare, showBusinessDialog, showC2CToast, preCheckC2CAct, init, C2CEntry, C2CMainDialog, C2CTipsDialog }
}

})